import * as React from 'react'
import {
  Cable,
  CheckCircle2,
  FlaskConical,
  LogIn,
  LoaderCircle,
  Pencil,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SheetFooter } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type {
  McpAuthType,
  McpAgent,
  McpConnection,
  McpConnectionTestResult,
  McpConnectionWrite,
} from '@/features/tools/types'
import {
  useCreateMcpConnection,
  useCompleteMcpOauth,
  useDeleteMcpConnection,
  useStartMcpOauth,
  useTestMcpConnection,
  useUpdateMcpConnection,
} from '@/hooks/useToolsAdmin'

const EMPTY: McpConnectionWrite = {
  name: '',
  server_url: '',
  auth_type: 'oauth_authorization_code',
  credential_ref: null,
  oauth_token_url: null,
  oauth_client_id: null,
  oauth_scopes: [],
  oauth_grant_scope: 'user',
  enabled_agents: [],
  enabled: false,
}
const MCP_OAUTH_CONNECTION_KEY = 'careforce:mcp-oauth-connection-id'

function draftFrom(connection?: McpConnection): McpConnectionWrite {
  if (!connection) return { ...EMPTY }
  return {
    name: connection.name,
    server_url: connection.server_url,
    auth_type: connection.auth_type,
    credential_ref: connection.credential_ref,
    oauth_token_url: connection.oauth_token_url,
    oauth_client_id: connection.oauth_client_id,
    oauth_scopes: connection.oauth_scopes,
    oauth_grant_scope: connection.oauth_grant_scope,
    enabled_agents: connection.enabled_agents,
    enabled: connection.enabled,
  }
}

function oauthConfigurationKey(connection: McpConnectionWrite): string {
  return JSON.stringify({
    server_url: connection.server_url,
    auth_type: connection.auth_type,
    oauth_scopes: [...(connection.oauth_scopes ?? [])].toSorted(),
    oauth_grant_scope: connection.oauth_grant_scope,
  })
}

function oauthStatus(connection?: McpConnection) {
  return (
    connection?.oauth_status ??
    (connection?.oauth_connected ? 'connected' : 'sign_in_required')
  )
}

export function ConnectionForm({
  connection,
  onClose,
  initialTestResult,
}: {
  connection?: McpConnection
  onClose: () => void
  initialTestResult?: McpConnectionTestResult
}) {
  const [draft, setDraft] = React.useState(() => draftFrom(connection))
  const [savedConnection, setSavedConnection] = React.useState<McpConnection>()
  const activeConnection = connection ?? savedConnection
  const create = useCreateMcpConnection()
  const update = useUpdateMcpConnection()
  const remove = useDeleteMcpConnection()
  const startOauth = useStartMcpOauth()
  const testConnection = useTestMcpConnection()
  const [testResult, setTestResult] = React.useState<
    McpConnectionTestResult | undefined
  >(initialTestResult)
  const [testedConfigurationKey, setTestedConfigurationKey] = React.useState<
    string | undefined
  >(() =>
    initialTestResult ? oauthConfigurationKey(draftFrom(connection)) : undefined
  )
  const [testError, setTestError] = React.useState<string>()
  const pending =
    create.isPending ||
    update.isPending ||
    remove.isPending ||
    startOauth.isPending ||
    testConnection.isPending
  const usesSignInOauth = draft.auth_type === 'oauth_authorization_code'
  const activeOauthStatus = oauthStatus(activeConnection)
  const oauthAuthorized = !usesSignInOauth || activeOauthStatus === 'connected'
  const draftConfigurationKey = oauthConfigurationKey(draft)
  const persistedConfigurationKey = activeConnection
    ? oauthConfigurationKey(draftFrom(activeConnection))
    : undefined
  const oauthConfigurationChanged = Boolean(
    activeConnection &&
      draftConfigurationKey !== persistedConfigurationKey &&
      (usesSignInOauth ||
        activeConnection.auth_type === 'oauth_authorization_code')
  )
  const currentTestResult =
    testedConfigurationKey === draftConfigurationKey ? testResult : undefined
  const connectionVerified = !usesSignInOauth || Boolean(currentTestResult)
  const angelicaAvailable =
    !usesSignInOauth || draft.oauth_grant_scope === 'organization'
  const canSave =
    !activeConnection || connectionVerified || oauthConfigurationChanged
  const set = <K extends keyof McpConnectionWrite>(
    key: K,
    value: McpConnectionWrite[K]
  ) => setDraft((current) => ({ ...current, [key]: value }))
  const setAgent = (agent: McpAgent, enabled: boolean) =>
    setDraft((current) => ({
      ...current,
      oauth_grant_scope:
        agent === 'angelica' && enabled
          ? 'organization'
          : current.oauth_grant_scope,
      enabled_agents: enabled
        ? Array.from(new Set([...current.enabled_agents, agent]))
        : current.enabled_agents.filter(
            (enabledAgent) => enabledAgent !== agent
          ),
    }))
  const setOauthGrantScope = (scope: McpConnectionWrite['oauth_grant_scope']) =>
    setDraft((current) => ({
      ...current,
      oauth_grant_scope: scope,
      enabled_agents:
        scope === 'user'
          ? current.enabled_agents.filter((agent) => agent !== 'angelica')
          : current.enabled_agents,
    }))

  const beginOauth = async (connectionId: string) => {
    const redirectUri = new URL('/tools', window.location.origin)
    window.sessionStorage.setItem(MCP_OAUTH_CONNECTION_KEY, connectionId)
    const started = await startOauth.mutateAsync({
      connectionId,
      redirectUri: redirectUri.toString(),
    })
    window.location.assign(started.authorization_url)
  }

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      if (activeConnection) {
        const connectionDraft = oauthConfigurationChanged
          ? { ...draft, enabled: false }
          : draft
        const saved = await update.mutateAsync({
          connectionId: activeConnection.id,
          connection: connectionDraft,
        })
        setSavedConnection(saved)
        toast.success('MCP connection updated')
        onClose()
      } else {
        const saved = await create.mutateAsync({
          ...draft,
          enabled: false,
        })
        setSavedConnection(saved)
        if (draft.auth_type === 'oauth_authorization_code') {
          try {
            await beginOauth(saved.id)
          } catch (error) {
            setTestError(
              error instanceof Error
                ? error.message
                : 'This server did not advertise a supported OAuth flow'
            )
            return
          }
        }
        toast.success('MCP connection draft created')
        onClose()
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save connection'
      )
    }
  }

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={save}>
      <div className="flex flex-col gap-4 overflow-y-auto px-5 py-5">
        <div className="grid gap-2">
          <Label htmlFor="mcp-name">Name</Label>
          <Input
            id="mcp-name"
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="Scheduling MCP"
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="mcp-url">MCP server URL</Label>
          <Input
            id="mcp-url"
            value={draft.server_url}
            onChange={(event) => set('server_url', event.target.value)}
            placeholder="https://example.com/mcp"
            type="url"
            required
          />
          <p className="text-xs text-muted-foreground">
            We’ll discover how this server authenticates and verify its live
            tool catalogue.
          </p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="mcp-auth">Authentication</Label>
          <Select
            value={draft.auth_type}
            onValueChange={(value) => set('auth_type', value as McpAuthType)}>
            <SelectTrigger id="mcp-auth" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No authentication</SelectItem>
              <SelectItem value="oauth_authorization_code">
                OAuth — sign in
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        {draft.auth_type === 'oauth_authorization_code' ? (
          <div className="grid gap-2">
            <p className="text-sm font-medium">Who can use this connection?</p>
            <ToggleGroup
              type="single"
              variant="outline"
              aria-label="Who can use this connection?"
              value={draft.oauth_grant_scope}
              onValueChange={(value) =>
                value &&
                setOauthGrantScope(
                  value as McpConnectionWrite['oauth_grant_scope']
                )
              }
              className="w-full">
              <ToggleGroupItem value="user" className="flex-1">
                Just me
              </ToggleGroupItem>
              <ToggleGroupItem value="organization" className="flex-1">
                My organization
              </ToggleGroupItem>
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              {draft.oauth_grant_scope === 'organization'
                ? 'Everyone in your organization can use this connected account.'
                : 'Only you can use this connected account.'}
            </p>
          </div>
        ) : null}
        {activeConnection && draft.auth_type === 'oauth_authorization_code' ? (
          <div className="rounded-lg border p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">Connection access</p>
                  <Badge variant="outline">
                    {oauthConfigurationChanged
                      ? 'Changes not verified'
                      : currentTestResult
                        ? 'Verified'
                        : activeOauthStatus === 'expired'
                          ? 'Reconnect required'
                          : oauthAuthorized
                            ? 'Authorized'
                            : 'Sign-in required'}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {oauthConfigurationChanged
                    ? 'Save these changes first. Careforce will disable agent access until you reconnect and test the updated connection.'
                    : currentTestResult
                      ? "The account is authorized and the server's live tool catalogue is available."
                      : activeOauthStatus === 'expired'
                        ? 'This authorization expired. Reconnect it before enabling this connection for an agent.'
                        : oauthAuthorized
                          ? 'The account is authorized. Test the server before enabling it for an agent.'
                          : 'Authorize an account before enabling this connection for an agent.'}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                {oauthAuthorized ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pending || oauthConfigurationChanged}
                    onClick={async () => {
                      setTestResult(undefined)
                      setTestedConfigurationKey(undefined)
                      setTestError(undefined)
                      try {
                        const result = await testConnection.mutateAsync(
                          activeConnection.id
                        )
                        setTestResult(result)
                        setTestedConfigurationKey(draftConfigurationKey)
                        toast.success(
                          `Connected successfully · ${result.tool_count} tools discovered`
                        )
                      } catch (error) {
                        const message =
                          error instanceof Error
                            ? error.message
                            : 'Failed to test MCP connection'
                        setTestError(message)
                        toast.error(message)
                      }
                    }}>
                    <FlaskConical className="size-4" />
                    {testConnection.isPending ? 'Testing…' : 'Test'}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pending || oauthConfigurationChanged}
                  onClick={async () => {
                    try {
                      await beginOauth(activeConnection.id)
                    } catch (error) {
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : 'Failed to start MCP sign-in'
                      )
                    }
                  }}>
                  <LogIn className="size-4" />
                  {oauthAuthorized ? 'Reconnect' : 'Sign in'}
                </Button>
              </div>
            </div>
            {testError ? (
              <p className="mt-3 text-xs text-destructive">{testError}</p>
            ) : null}
            {currentTestResult ? (
              <div
                data-testid="mcp-connection-test-result"
                className="mt-3 rounded-lg border border-success-border bg-success-ui-bg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-success-solid">
                  <CheckCircle2 className="size-4" />
                  Connected successfully
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {currentTestResult.tool_count} tools discovered in{' '}
                  {currentTestResult.duration_ms} ms
                </p>
                {currentTestResult.tool_names.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {currentTestResult.tool_names.map((name) => (
                      <Badge key={name} variant="outline" className="font-mono">
                        {name}
                      </Badge>
                    ))}
                    {currentTestResult.tool_count >
                    currentTestResult.tool_names.length ? (
                      <Badge variant="outline">
                        +
                        {currentTestResult.tool_count -
                          currentTestResult.tool_names.length}{' '}
                        more
                      </Badge>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="rounded-lg border p-3">
          <p className="text-sm font-medium">Enabled agents</p>
          <p className="mb-3 text-xs text-muted-foreground">
            {activeConnection && connectionVerified
              ? 'Choose which Careforce agents may attach this dynamic toolset.'
              : 'Choose intended agent access. The connection stays off until verified.'}
          </p>
          <div className="flex items-center justify-between py-1">
            <Label htmlFor="mcp-david">David</Label>
            <Switch
              id="mcp-david"
              checked={draft.enabled_agents.includes('david')}
              disabled={pending}
              onCheckedChange={(value) => setAgent('david', value === true)}
            />
          </div>
          <div className="flex items-center justify-between py-1">
            <div>
              <Label htmlFor="mcp-angelica">Angelica</Label>
              <p className="text-xs text-muted-foreground">
                Only available for organization-shared connections
              </p>
            </div>
            <Switch
              id="mcp-angelica"
              checked={draft.enabled_agents.includes('angelica')}
              disabled={pending || !angelicaAvailable}
              onCheckedChange={(value) => setAgent('angelica', value === true)}
            />
          </div>
        </div>
      </div>
      <SheetFooter className="flex-row justify-between border-t border-border bg-neutral-ui-bg">
        {activeConnection ? (
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={async () => {
              try {
                await remove.mutateAsync(activeConnection.id)
                toast.success('MCP connection deleted')
                onClose()
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : 'Failed to delete connection'
                )
              }
            }}>
            {remove.isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending || !canSave}>
            {activeConnection ? 'Save changes' : 'Continue'}
          </Button>
        </div>
      </SheetFooter>
    </form>
  )
}

export const McpConnectionsPanel = React.memo(function McpConnectionsPanel({
  connections,
  onEdit,
}: {
  connections: McpConnection[] | undefined
  onEdit: (
    connection: McpConnection,
    initialTestResult?: McpConnectionTestResult
  ) => void
}) {
  const update = useUpdateMcpConnection()
  const completeOauth = useCompleteMcpOauth()
  const testConnection = useTestMcpConnection()
  const handledOauthCallback = React.useRef(false)
  const [resumeEditor, setResumeEditor] = React.useState<{
    connectionId: string
    testResult?: McpConnectionTestResult
  }>()
  const [verifiedResults, setVerifiedResults] = React.useState<
    Record<string, McpConnectionTestResult>
  >({})

  React.useEffect(() => {
    if (handledOauthCallback.current) return
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const oauthError = params.get('error')
    if (!state || (!code && !oauthError)) return
    handledOauthCallback.current = true
    const pendingConnectionId = window.sessionStorage.getItem(
      MCP_OAUTH_CONNECTION_KEY
    )
    window.sessionStorage.removeItem(MCP_OAUTH_CONNECTION_KEY)
    params.delete('code')
    params.delete('state')
    params.delete('error')
    params.delete('error_description')
    const query = params.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    )
    if (oauthError) {
      toast.error('MCP sign-in was cancelled or denied')
      if (pendingConnectionId) {
        setResumeEditor({ connectionId: pendingConnectionId })
      }
      return
    }
    void completeOauth
      .mutateAsync({ code: code as string, state })
      .then(async (completed) => {
        const connectionId = completed.grant.connection_id
        try {
          const result = await testConnection.mutateAsync(connectionId)
          setVerifiedResults((current) => ({
            ...current,
            [connectionId]: result,
          }))
          setResumeEditor({ connectionId, testResult: result })
          toast.success(
            `Connected successfully · ${result.tool_count} tools discovered`
          )
        } catch (testError) {
          setResumeEditor({ connectionId })
          toast.error(
            testError instanceof Error
              ? `Connected, but verification failed: ${testError.message}`
              : 'Connected, but verification failed'
          )
        }
        return undefined
      })
      .catch((callbackError) => {
        if (pendingConnectionId) {
          setResumeEditor({ connectionId: pendingConnectionId })
        }
        toast.error(
          callbackError instanceof Error
            ? callbackError.message
            : 'Failed to complete MCP sign-in'
        )
      })
  }, [completeOauth, testConnection])

  React.useEffect(() => {
    if (!resumeEditor || !connections) return
    const connection = connections.find(
      (item) => item.id === resumeEditor.connectionId
    )
    if (!connection) return
    onEdit(connection, resumeEditor.testResult)
    setResumeEditor(undefined)
  }, [connections, onEdit, resumeEditor])

  return (
    <>
      {connections?.map((connection) => {
        const isToggling =
          update.isPending &&
          update.variables?.connectionId === connection.id &&
          typeof update.variables.connection.enabled === 'boolean'
        const displayedEnabled = isToggling
          ? update.variables?.connection.enabled === true
          : connection.enabled

        return (
          <div
            key={connection.id}
            className="flex items-center gap-3 rounded-lg border border-neutral-ui-border p-3">
            <Cable className="size-5 shrink-0 text-primary-high-contrast" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{connection.name}</span>
                {connection.auth_type === 'oauth_authorization_code' ? (
                  <Badge variant="outline">
                    {oauthStatus(connection) === 'connected'
                      ? 'Connected'
                      : oauthStatus(connection) === 'expired'
                        ? 'Reconnect required'
                        : 'Sign-in required'}
                  </Badge>
                ) : null}
                {connection.enabled_agents.includes('david') ? (
                  <Badge variant="outline">David</Badge>
                ) : null}
                {connection.enabled_agents.includes('angelica') ? (
                  <Badge variant="outline">Angelica</Badge>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isToggling ? (
                <LoaderCircle
                  aria-label={`Updating ${connection.name}`}
                  className="size-4 animate-spin text-muted-foreground"
                />
              ) : null}
              <Switch
                checked={displayedEnabled}
                disabled={update.isPending}
                aria-label={`${displayedEnabled ? 'Disable' : 'Enable'} ${connection.name}`}
                onCheckedChange={async (value) => {
                  if (
                    value === true &&
                    connection.auth_type === 'oauth_authorization_code' &&
                    !verifiedResults[connection.id]
                  ) {
                    onEdit(connection, verifiedResults[connection.id])
                    toast.info('Test this connection before enabling it')
                    return
                  }
                  try {
                    await update.mutateAsync({
                      connectionId: connection.id,
                      connection: { enabled: value === true },
                    })
                    toast.success(
                      `${connection.name} ${value === true ? 'enabled' : 'disabled'}`
                    )
                  } catch (error) {
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : `Failed to ${value === true ? 'enable' : 'disable'} connection`
                    )
                  }
                }}
              />
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Edit ${connection.name}`}
              onClick={() =>
                onEdit(connection, verifiedResults[connection.id])
              }>
              <Pencil className="size-4" />
            </Button>
          </div>
        )
      })}
    </>
  )
})
